import { AxiosInstance } from 'axios';
import axiosClient from './axiosClientConfig';
import {
	CreateCase,
	ListCasesRequest,
	CreateCaseResponse,
	ListCasesLawyerResponse,
	CasePhases,
	CaseLawyerStatus,
	CaseMetricsData,
	CaseDetailsLawyer,
	ActionRequiredLawyerData,
	ActionStatus,
	NotesResponse,
	PinnedCases,
	CaseSettlementDetails,
	UsersToPay,
	PaymentDistribution,
	DocumentType,
	SaveCaseSettlement,
	CaseSettlementUsersSign,
	ClaimFormUser,
	CreateDocumentsToSend,
	DocumentsToSendResponse,
	State,
	Pagination,
	SettlementSummary,
	PaymentSummary,
} from '@/interfaces';
import { HttpResponse } from 'interfaces/httpClient';
import { getValidCaseId } from '@/utils';

class CasesLawyerService {
	constructor(private client: AxiosInstance) {}

	async save(props: CreateCase): Promise<HttpResponse<CreateCaseResponse>> {
		return this.client.post('/cases_lawyers/save', { ...props });
	}

	async complete(
		props: Partial<CreateCase>
	): Promise<HttpResponse<CreateCaseResponse>> {
		return this.client.post('/cases_lawyers/complete', { ...props });
	}

	async delete(caseId: string): Promise<HttpResponse<CreateCaseResponse>> {
		return this.client.delete(`/cases_lawyers/${caseId}`);
	}

	async abort(caseId: string): Promise<HttpResponse<any>> {
		return this.client.delete(`/cases_lawyers/${caseId}/abort`);
	}

	async get({
		search,
		page,
		phase,
		status,
		alphabetical,
		limit,
		archived,
	}: ListCasesRequest): Promise<HttpResponse<ListCasesLawyerResponse>> {
		const params = {
			page,
			limit: limit || 9,
			...(search && { search }),
			...(phase && { phaseId: phase }),
			...(status && { statusId: status }),
			...(alphabetical && { isOrderAlph: alphabetical }),
			...(archived && { archived }),
		};
		return this.client.get(`/cases_lawyers`, { params });
	}

	async archiveCase(caseId: string): Promise<HttpResponse<any>> {
		return this.client.put(`/cases_lawyers/${caseId}/archive`);
	}

	async unarchiveCase(caseId: string): Promise<HttpResponse<any>> {
		return this.client.delete(`/cases_lawyers/${caseId}/archive`);
	}

	async getCasePhases(): Promise<HttpResponse<CasePhases[]>> {
		return this.client.get(`/cases_lawyers/phases`);
	}

	async getCaseStatuses(): Promise<HttpResponse<CaseLawyerStatus[]>> {
		return this.client.get(`/cases_lawyers/status`);
	}

	async pinCase(caseId: string) {
		return this.client.patch(`/cases_lawyers/${caseId}/pinned-case`);
	}

	async hideShowCase(caseId: string, visible: boolean) {
		return this.client.patch(`/cases_lawyers/${caseId}/visibility`, {
			visible,
		});
	}

	async getCaseDetails(
		caseId: string
	): Promise<HttpResponse<CaseDetailsLawyer>> {
		const validCaseId = getValidCaseId(caseId);
		if (!validCaseId) {
			throw new Error('caseId is required to fetch case details');
		}
		return this.client.get(`/cases_lawyers/${validCaseId}`);
	}

	async getCaseMetrics(caseId: string): Promise<HttpResponse<CaseMetricsData>> {
		return this.client.get(`/cases_lawyers/metrics/${caseId}`);
	}

	async getActionRequired(
		caseId: string,
		limit?: number,
		page?: number,
		search?: string,
		color?: ActionStatus
	): Promise<HttpResponse<ActionRequiredLawyerData>> {
		const auxParams = [];
		limit && auxParams.push(`limit=${limit}`);
		page && auxParams.push(`page=${page}`);
		search && auxParams.push(`search=${search}`);
		color && auxParams.push(`color=${color}`);
		const params = auxParams.join('&');
		return this.client.get(
			params.length > 0
				? `/cases_lawyers/actions-required/${caseId}?${params}`
				: `/cases_lawyers/actions-required/${caseId}`
		);
	}

	async getPinnedLawyerCases(): Promise<HttpResponse<PinnedCases[]>> {
		return this.client.get('/cases_lawyers/list-pinned-cases');
	}

	// Applicant Intake
	async getLawyerSign(
		caseId: string
	): Promise<
		HttpResponse<{ retain: string | null; preservation_date: string | null }>
	> {
		return this.client.get(`/cases-applicant/${caseId}/lawyer-sign`);
	}

	async saveLawyerSign(sign: string, date: string, caseId: string) {
		return this.client.post(`/cases-applicant/${caseId}/lawyer-sign`, {
			lawyer_sign: sign,
			preservation_date: date,
		});
	}

	async completeLawyerSign(sign: string, date: string, caseId: string) {
		return this.client.put(`/cases-applicant/${caseId}/lawyer-sign`, {
			lawyer_sign: sign,
			preservation_date: date,
		});
	}

	async getRetainerByCaseId(id: string) {
		return this.client.get(`/cases-applicant/${id}/retainer-info`);
	}

	async getDocuments(
		id: string
	): Promise<HttpResponse<DocumentsToSendResponse[]>> {
		return this.client.get(`/action-required/retainer-setup/${id}`);
	}

	async saveDocument(data: CreateDocumentsToSend): Promise<HttpResponse<void>> {
		return this.client.post(`/action-required/create-retainer-setup/`, {
			...data,
		});
	}

	async deleteDocument(caseId: string, setupId: string) {
		return this.client.delete(
			`/action-required/retainer-setup/${caseId}/${setupId}`
		);
	}

	// Active Case Phase

	async sendActiveCaseDocument(caseId: string, type: DocumentType) {
		return this.client.post(
			`/cases-active/send-active-case-document/${caseId}`,
			{ documentType: type }
		);
	}

	async sendOptDocument(caseId: string) {
		return this.client.post(`/cases-active/send-opt-document/${caseId}`);
	}

	async unsuccessful(caseId: string) {
		return this.client.post(`/cases-active/case-unsuccesful/${caseId}`);
	}

	// Settlement Phase

	async caseSettlementDetail(
		caseId: string
	): Promise<HttpResponse<CaseSettlementDetails>> {
		return this.client.get(`/cases-settlement/detail/${caseId}`);
	}

	async caseSettlementUserSigns(
		caseId: string,
		page: number,
		limit: number
	): Promise<HttpResponse<CaseSettlementUsersSign>> {
		return this.client.get(
			`/cases-settlement/user-signs-settlement/${caseId}?page=${page}&limit=${limit}`
		);
	}

	async caseSettlementSave(caseId: string, body: SaveCaseSettlement) {
		const formData = new FormData();
		body.total_amount &&
			formData.append('total_amount', String(body.total_amount));
		body.attorney_fees &&
			formData.append('attorney_fees', String(body.attorney_fees));
		body.type_of_settlement &&
			formData.append('type_of_settlement', String(body.type_of_settlement));
		body.date_of_settlement &&
			formData.append('date_of_settlement', body.date_of_settlement);
		body.attorney_expenses &&
			formData.append('attorney_expenses', String(body.attorney_expenses));
		body.file && formData.append('file', body.file);

		return this.client.post(`/cases-settlement/save/${caseId}`, formData, {
			headers: {
				'Content-Type': 'multipart/form-data',
			},
		});
	}

	async caseSettlementComplete(caseId: string, body: SaveCaseSettlement) {
		const formData = new FormData();
		formData.append('total_amount', String(body.total_amount));
		formData.append('attorney_fees', String(body.attorney_fees));
		formData.append('type_of_settlement', String(body.type_of_settlement));
		formData.append('date_of_settlement', body.date_of_settlement);
		formData.append('attorney_expenses', String(body.attorney_expenses));
		body.file && formData.append('file', body.file);

		return this.client.put(`/cases-settlement/complete/${caseId}`, formData, {
			headers: {
				'Content-Type': 'multipart/form-data',
			},
		});
	}

	async getUsersToPay(
		caseId: string,
		page?: number,
		limit?: number,
		search?: string,
		state_ids?: number[]
	): Promise<HttpResponse<UsersToPay>> {
		const auxParams = [];
		limit && auxParams.push(`limit=${limit}`);
		page && auxParams.push(`page=${page}`);
		search && auxParams.push(`search=${search}`);
		state_ids && auxParams.push(`state_ids=${state_ids}`);
		const params = auxParams.join('&');
		return this.client.get(
			params
				? `/cases-settlement/user-applications/${caseId}?${params}`
				: `/cases-settlement/user-applications/${caseId}`
		);
	}

	async getStates(
		page?: number,
		limit?: number,
		search?: string
	): Promise<HttpResponse<Pagination & { items: State[] }>> {
		const auxParams = [];
		limit && auxParams.push(`limit=${limit}`);
		page && auxParams.push(`page=${page}`);
		search && auxParams.push(`search=${search}`);
		const params = auxParams.join('&');
		return this.client.get(
			params ? `/cases-settlement/states?${params}` : `/cases-settlement/states`
		);
	}

	async getSettlementSummary(
		caseId: string
	): Promise<HttpResponse<SettlementSummary>> {
		return this.client.get(`/cases-settlement/summary/${caseId}`);
	}

	async getPaymentSummary(
		caseId: string
	): Promise<HttpResponse<PaymentSummary>> {
		return this.client.get(`/cases-settlement/summary/payment/${caseId}`);
	}

	async completePaymentUsers(caseId: string, request: PaymentDistribution) {
		return this.client.put(
			`/cases-settlement/payment-distribution/${caseId}/complete`,
			{ ...request }
		);
	}

	async confirmPaymentByUser(applicationId: number, amount: number) {
		return this.client.put(
			`/cases-settlement/payment-distribution/user/${applicationId}`,
			{ amount }
		);
	}

	async confirmSettlementAgreement(caseId: string) {
		return this.client.post(
			`/cases-settlement/send-settlement-agreement/${caseId}`
		);
	}

	async confirmPaySettlement(caseId: string) {
		return this.client.post(`/cases-settlement/pay-settlement/${caseId}`);
	}

	async resolvedCase(caseId: string) {
		return this.client.post(`/cases-settlement/case-resolved/${caseId}`);
	}

	// Notes
	async getNotes(
		caseId: string,
		page: number
	): Promise<HttpResponse<NotesResponse>> {
		return this.client.get(`/cases/${caseId}/notes?page=${page}&limit=${30}`);
	}

	async createNote(
		caseId: string,
		message: string
	): Promise<HttpResponse<any>> {
		return this.client.post(`/cases/${caseId}/notes`, { content: message });
	}

	async getClaimFormData(path: string): Promise<HttpResponse<ClaimFormUser>> {
		return this.client.get(`${path}`);
	}
}

export const casesLawyerService = new CasesLawyerService(axiosClient);
